import { Input } from "@/components/ui/Input";
import { FormControl } from "@/components/ui/FormControl";
import { clsx } from "clsx";
import classes from "./YourAddress.module.scss";
import type { AddressFields } from "@/types/Address";
import type { YourAddressProps } from "./YourAddressProps";
import { useTranslation } from "@/hooks/useTranslation";
import { Select } from "@/components/ui/Select";
import { countryNames } from "@/utils/countries";
import { Field } from "../Field";
import * as z from "zod";
import React from "react";

export type YourAddressFormProps = {
  className?: string;
  config: YourAddressProps["config"];
};

type AddressFieldConfig = {
  name: keyof AddressFields;
  serverErrorPath: string;
  errorKey: string;
  type?: "input" | "select";
};

const ADDRESS_FIELDS: AddressFieldConfig[] = [
  {
    name: "address-line-1",
    serverErrorPath: "your_address.address_line_1",
    errorKey: "address-line-1-required",
  },
  {
    name: "address-line-2",
    serverErrorPath: "your_address.address_line_2",
    errorKey: "address-line-2-required",
  },
  {
    name: "city",
    serverErrorPath: "your_address.city",
    errorKey: "city-required",
  },
  {
    name: "region",
    serverErrorPath: "your_address.region",
    errorKey: "region-required",
  },
  {
    name: "postal-code",
    serverErrorPath: "your_address.postal_code",
    errorKey: "postal-code-required",
  },
  {
    name: "country",
    serverErrorPath: "your_address.country",
    errorKey: "country-required",
    type: "select",
  },
];

export function YourAddressForm(props: YourAddressFormProps) {
  const { className, config } = props;
  const t = useTranslation("your_address");

  const createValidationSchema =
    (fieldName: keyof AddressFields, errorKey: string) => () => {
      if (config["your-address-fields"][fieldName]["is-required"]) {
        return z.string().min(1, {
          message: t(`errors.${errorKey}`) as string,
        });
      }
      return null;
    };

  const renderInputField = (
    value: string,
    handleChange: (
      value: string,
      ev: React.ChangeEvent<HTMLInputElement>,
    ) => void,
    isValid: boolean | null,
    errorMessage: string | null,
    handleBlur: () => void,
    ref: React.Ref<HTMLInputElement>,
    fieldName: keyof AddressFields,
  ) => (
    <FormControl
      label={t(config["your-address-fields"][fieldName].label.i18n)}
      error={errorMessage}
      isValid={isValid}
    >
      <Input
        name={fieldName}
        value={value}
        onChange={(ev) => handleChange(ev.target.value, ev)}
        onBlur={() => handleBlur()}
        ref={ref}
      />
    </FormControl>
  );

  const renderSelectField = (
    value: string,
    handleChange: (
      value: string,
      ev: React.ChangeEvent<HTMLSelectElement>,
    ) => void,
    isValid: boolean | null,
    errorMessage: string | null,
    handleBlur: () => void,
    ref: React.Ref<HTMLSelectElement>,
    fieldName: keyof AddressFields,
  ) => (
    <FormControl
      label={t(config["your-address-fields"][fieldName].label.i18n)}
      error={errorMessage}
      isValid={isValid}
    >
      <Select
        name={fieldName}
        value={value}
        onChange={(ev) => handleChange(ev.target.value, ev)}
        onBlur={() => handleBlur()}
        ref={ref}
      >
        <option value=""></option>
        {countryNames.map((country) => (
          <option key={country} value={country}>
            {country}
          </option>
        ))}
      </Select>
    </FormControl>
  );

  const renderAddressField = (fieldConfig: AddressFieldConfig) => (
    <Field<AddressFields>
      key={fieldConfig.name}
      serverErrorPaths={[fieldConfig.serverErrorPath]}
      name={fieldConfig.name}
      validate={createValidationSchema(fieldConfig.name, fieldConfig.errorKey)}
      render={({
        value,
        handleChange,
        isValid,
        errorMessage,
        handleBlur,
        ref,
      }) => {
        if (fieldConfig.type === "select") {
          return renderSelectField(
            value,
            handleChange,
            isValid,
            errorMessage,
            handleBlur,
            ref as React.RefObject<HTMLSelectElement>,
            fieldConfig.name,
          );
        }

        return renderInputField(
          value,
          handleChange,
          isValid,
          errorMessage,
          handleBlur,
          ref as React.RefObject<HTMLInputElement>,
          fieldConfig.name,
        );
      }}
    />
  );

  return (
    <div className={clsx(classes.form, className)}>
      {ADDRESS_FIELDS.map(renderAddressField)}
    </div>
  );
}
